import { CircularProgress, Divider, InputAdornment, useTheme, Typography, Stack, chipClasses, selectClasses, inputBaseClasses, outlinedInputClasses } from "@mui/material";
import { deepmerge } from "@mui/utils";
import React, { useState, useLayoutEffect } from "react";
import { capitalizeAndCleanUnderscore } from "@utils";
import AddIcon from "src/icons/AddIcon";
import ChevronDown from "src/icons/ChevronDown";
import SearchBar from "@hyro-components/SearchBar";
import { StyledTextField } from "@hyro-components/fields/text-field/HyroTextField.styles";
import { useSingleSelect } from "./HyroSingleSelect.hook";
import { SELECT_MODES } from "./HyroSingleSelect.consts";
import { MenuSx, StyledMenuItem, StyledSearchBar, InputSx, SelectSx } from "./HyroSingleSelect.styles";
import Chip from "@hyro-components/Chip";
import HyroTruncateTypography from "@hyro-components/hyro-truncate-typography/HyroTruncateTypography";
import Tooltip from "@hyro-components/Tooltip";

const MAX_WIDTH = "320px";
const EMPTY_ARRAY = [];
const EMPTY_OBJ = {};

const HyroSingleSelect = ({
  id,
  value,
  showLabel,
  label,
  options = EMPTY_ARRAY,
  onChange,
  onFocus,
  onBlur,
  onPopperClose,
  onMenuClose,
  error,
  isReadOnly,
  onAddNewCollection,
  isFieldReadOnlyFromSpec,
  placeholder,
  loading,
  field,
  testId,
  typography = {
    label: "body1",
    description: "body2"
  },
  open = false,
  helperText,
  SelectProps = EMPTY_OBJ,
  noOptionsMessage,
  capitalizeOption = true,
  fullWidth = true,
  disableSearch = false,
  valueLines = 2,
  renderValueChip,
  renderOptionChip,
  mode = SELECT_MODES.SYNC,
  onSearchChange,
  selectedFirst = false,
  matchAnchorWidth = false,
  ...rest
}) => {
  const theme = useTheme();
  const isAsync = mode === SELECT_MODES.ASYNC && typeof onSearchChange === "function";
  const { menuOpen,
    setMenuOpen,
    inputRef,
    filteredOptions,
    optionsExist,
    showSearch,
    handleOpen,
    handleChange,
    handleSearch,
    ADD_NEW,
    showPlaceholder
  } = useSingleSelect({ options, field, noOptionsMessage, onFocus, onPopperClose, onAddNewCollection, onChange, onSearchChange, isAsync, open, value, placeholder, loading, selectedFirst });

  const [anchorWidth, setAnchorWidth] = useState(null);
  useLayoutEffect(() => {
    if (matchAnchorWidth && inputRef.current?.node) {
      setAnchorWidth(inputRef.current.node.clientWidth);
    }
  }, [matchAnchorWidth, menuOpen]);
  const anchorMeasuredWidth = anchorWidth ?? (inputRef.current ? inputRef.current.node.clientWidth : "auto");

  return (
    <StyledTextField
      disabled={!isAsync && !!loading}
      readOnly={isReadOnly}
      isFieldReadOnlyFromSpec={isFieldReadOnlyFromSpec}
      isMultiLineSelect={true}
      data-testid={testId}
      slotProps={{
        select: {
          "data-testid": `${testId}-select`,
          renderValue: () => {
            const selectedOption = options?.find(option => option.key === value);
            let displayValue;
            // Show the resolved label if we have it; otherwise "Loading..." while it's still
            // resolving (async only shows this once a value is set, so the id isn't flashed);
            // otherwise fall back to the raw value (async) or empty (sync).
            if (selectedOption) displayValue = selectedOption.display_key;
            else if (loading && (value || !isAsync)) displayValue = "Loading...";
            else displayValue = isAsync ? (value ?? "") : "";
            const SelectedIcon = selectedOption?.Icon;
            const valueTypography = <HyroTruncateTypography
              variant={typography.label}
              maxWidth="100%"
              lines={valueLines}>
              {displayValue}
            </HyroTruncateTypography>;
            // Options may carry an `Icon` (e.g. Quick Actions topics); show it beside the value.
            const valueText = SelectedIcon
              ? <Stack direction="row" alignItems="center" gap={1} sx={{ overflow: "hidden" }}>
                <SelectedIcon />
                {valueTypography}
              </Stack>
              : valueTypography;
            // Consumers can render their own adornment (e.g. a status chip) next to the value.
            const valueChip = renderValueChip?.(selectedOption);
            if (!valueChip) return valueText;
            return <Stack direction="row" alignItems="center" gap={1} sx={{ overflow: "hidden" }}>
              {valueText}
              {valueChip}
            </Stack>;
          },
          inputRef,
          sx: SelectProps?.sx,
          displayEmpty: true,
          MenuProps: {
            autoFocus: false,
            sx: {
              ...MenuSx,
              ...SelectProps?.MenuProps?.sx
            },
            PaperProps: {
              style: {
                width: matchAnchorWidth ? anchorMeasuredWidth : "fit-content",
                minWidth: anchorMeasuredWidth,
                maxWidth: matchAnchorWidth ? "none" : MAX_WIDTH,
                maxHeight: onAddNewCollection ? "315px" : "205px",
                ...SelectProps?.MenuProps?.PaperProps?.style
              }
            },
            elevation: 3,
            anchorOrigin: SelectProps?.MenuProps?.anchorOrigin ?? {
              vertical: "bottom",
              horizontal: "left"
            },
            transformOrigin: SelectProps?.MenuProps?.transformOrigin ?? {
              vertical: "top",
              horizontal: "left"
            },
            ...onMenuClose ? {
              onClose: () => {
                onMenuClose?.();
              }
            } : {}
          },
          IconComponent: (props) => !loading ? <ChevronDown
            {...props}
            stroke={isReadOnly ? theme.palette.system[400] : theme.palette.text[700]} style={{ top: "unset", right: 12 }}
          /> : null,
          open: menuOpen,
          onClose: () => {
            onBlur?.();
            handleSearch("");
            setMenuOpen(false);
            if (inputRef.current?.node) {
              inputRef.current.node.parentElement.classList.remove("Mui-focused");
            }
          },
          onOpen: () => {
            handleOpen();
            if (inputRef.current?.node) {
              inputRef.current.node.parentElement.classList.add("Mui-focused");
            }
          }
        },
        formHelperText: {
          "data-testid": `${testId}-helper`
        },
        inputLabel: {
          "data-testid": `${testId}-label`
        },
        input: {
          sx: InputSx(loading),
          readOnly: isReadOnly,
          endAdornment: loading ? <InputAdornment sx={{ position: "absolute", right: 12 }} position="end" data-testid={`${testId}-loading`}>
            <CircularProgress color='inherit' size={20} />
          </InputAdornment> : null
        }
      }}
      label={showLabel ? label : ""}
      onChange={handleChange}
      error={!!error}
      helperText={helperText ?? ""}
      select
      fullWidth={fullWidth}
      sx={deepmerge(
        {
          ...SelectSx(showPlaceholder, placeholder || "Select an option"),
          [`& .${inputBaseClasses.root}.${outlinedInputClasses.root}.${inputBaseClasses.formControl}`]: {
            minHeight: "44px"
          }
        },
        SelectProps?.textFieldSx ?? {}
      )}
      id={id}
      value={value ?? ""}
      {...rest}
    >
      {showSearch && !disableSearch && <StyledSearchBar>
        <SearchBar stayOpen onChange={handleSearch} customWidth="100%" testId={`${testId}-search`} />
      </StyledSearchBar>}

      {onAddNewCollection &&
        <StyledMenuItem key={ADD_NEW} value={ADD_NEW}>
          <Stack direction={"row"} alignItems={"center"}>
            <div style={{ display: "flex", marginRight: "8px" }}>
              <AddIcon color={theme.palette.text[800]} />
            </div>
            Add New {label}
          </Stack>
        </StyledMenuItem>}

      {filteredOptions?.length > 0 && optionsExist && onAddNewCollection && (
        <Divider sx={{ borderColor: "rgba(45, 44, 68, 0.12)", marginBlock: "4px !important" }} />
      )}

      {(optionsExist || !onAddNewCollection) && filteredOptions?.map((option, optionIndex) => {
        const OptionIcon = option.Icon;
        const menuItem = <StyledMenuItem key={`${option.key}-${optionIndex}`} value={option.key} disabled={option.disabled} error={option.error} data-testid={`${testId}-option-${optionIndex}`}>
          <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} width={"100%"}>
            <Stack direction={"row"} alignItems={"center"} gap={1} sx={{ overflow: "hidden" }}>
              {OptionIcon && <OptionIcon />}
              <Stack direction={"column"} alignItems={"flex-start"} gap={0.5} sx={{ overflow: "hidden" }}>
                <Typography variant={typography.label} sx={{ width: "100%", whiteSpace: "wrap" }}>{capitalizeOption ? capitalizeAndCleanUnderscore(option.display_key) : option.display_key}</Typography>
                {option.description && <Typography variant={typography.description} color="text.700" noWrap sx={{ width: "100%" }} className="description" >{option.description}</Typography>}
              </Stack>
            </Stack>
            {renderOptionChip
              ? renderOptionChip(option)
              : (option.showNewChip || option.chipText) && (
                <Chip
                  label={option.chipText || "New"}
                  size="small"
                  isRounded={true}
                  textColorPath={["system", "0"]}
                  backgroundColorPath={option.chipText ? ["grey", "500"] : ["secondary", "main"]}
                  sx={{
                    marginLeft: "auto",
                    height: "20px",
                    [`& .${chipClasses.label}`]: {
                      padding: "1px 8px",
                    }
                  }}
                />
              )
            }
          </Stack>
        </StyledMenuItem>;

        if (option.disabled && option.disabledTooltipTitle) {
          return <Tooltip key={`${option.key}-${optionIndex}-disabled-tooltip`} title={option.disabledTooltipTitle} popperStyle={{ marginBottom: "-5px" }}>
            {menuItem}
          </Tooltip>;
        }

        return menuItem;
      })}

    </StyledTextField>
  );
};

export default HyroSingleSelect;
